import React from 'react';

import Checkbox from '../Checkbox';

import { OutputGroup, OutputGroupId, ReprocessSelection } from './reprocessOptions';
import styles from './ReprocessFlow.module.scss';

type OutputsPickerProps = {
  groups: OutputGroup[];
  value: ReprocessSelection;
  onChange: (next: ReprocessSelection) => void;
  disabled?: boolean;
};

/** Grouped outputs with a select-all per group. */
export const OutputsPicker: React.FC<OutputsPickerProps> = ({ groups, value, onChange, disabled }) => {
  const set = (id: OutputGroupId, keys: string[]) => onChange({ ...value, [id]: keys });

  if (!groups.length) {
    return (
      <p className={styles.hint}>
        Nothing here can be reprocessed for this selection. Configure conversions or transcodes in
        Settings first.
      </p>
    );
  }

  return (
    <div className={styles.groups}>
      {groups.map((group) => {
        const picked = value[group.id];
        const all = group.options.map((o) => o.key);
        const allOn = all.length > 0 && all.every((k) => picked.includes(k));
        return (
          <fieldset key={group.id} className={styles.group} data-test={`reprocess-group-${group.id}`}>
            <legend className={styles.groupHead}>
              <span className={styles.groupTitle}>{group.title}</span>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => set(group.id, allOn ? [] : all)}
                disabled={disabled}
              >
                {allOn ? 'Clear' : 'Select all'}
              </button>
            </legend>
            <div className={styles.optionGrid}>
              {group.options.map((o) => (
                <Checkbox
                  key={o.key}
                  className={styles.checkboxItem}
                  datatest={`reprocess-option-${o.key}`}
                  disabled={disabled}
                  label={
                    o.hint && o.hint !== o.label ? (
                      <span>
                        {o.label}
                        <code className={styles.key}>{o.hint}</code>
                      </span>
                    ) : (
                      o.label
                    )
                  }
                  checked={picked.includes(o.key)}
                  onChange={(e) =>
                    set(
                      group.id,
                      e.target.checked ? [...picked, o.key] : picked.filter((k) => k !== o.key),
                    )
                  }
                />
              ))}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
};

export default OutputsPicker;
